import { useState } from "react";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import { Button } from "@/common/components/ui/button";
import { Loader2, Plus, Globe, FileText } from "lucide-react";
import UploadService from "@/services/APIs/UploadService";
import { notify } from "@/common/components/common/Notification";
import { cn } from "@/lib/utils";

interface FileUploadFieldProps {
  name: string;
  type: "logo" | "pdfs";
  label: string;
  value: string;
  onChange: (e: { target: { name: string; value: string } }) => void;
  placeholder: string;
  readOnly?: boolean;
}

const FileUploadField = ({
  name,
  type,
  label,
  value,
  onChange,
  placeholder,
  readOnly = false,
}: FileUploadFieldProps) => {
  const readOnlyClass = readOnly ? "bg-slate-50 cursor-not-allowed" : "";
  const [uploading, setUploading] = useState(false);
  const url = value ?? "";

  // Handle local URL updates
  const handleUrlChange = (newUrl: string) => {
    if (onChange) {
      onChange({ target: { name, value: newUrl } });
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    setUploading(true);
    try {
      const response = await UploadService.uploadFiles({
        folderName: type === "logo" ? "ipo-logos" : "ipo-documents",
        files: [file],
      });

      // Assuming the response structure - adjust based on actual API response
      const fileUrl =
        response?.data?.url ?? response?.url ?? response?.file_url;
      if (fileUrl) {
        handleUrlChange(fileUrl);
        notify.success("File uploaded successfully!");
      } else {
        notify.error("Upload failed: Invalid response");
      }
    } catch (error: any) {
      console.error(error);
      notify.error(error?.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            name={name}
            value={url}
            onChange={e => handleUrlChange(e.target.value)}
            placeholder={placeholder}
            disabled={readOnly}
            className={`h-10 border-slate-200 pr-24 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
          />
          {!readOnly && (
            <div className="absolute bottom-1 right-1 top-1 flex items-center">
              <Label
                htmlFor={`file-upload-${name}`}
                className={cn(
                  "flex h-full cursor-pointer items-center gap-1.5 rounded-md bg-slate-100 px-3 text-[10px] font-bold uppercase text-slate-600 transition-all hover:bg-slate-200",
                  uploading && "cursor-wait opacity-50"
                )}
              >
                {uploading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Plus className="h-3 w-3" />
                )}
                Upload
              </Label>
              <input
                id={`file-upload-${name}`}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept={type === "logo" ? "image/*" : ".pdf"}
                disabled={uploading}
              />
            </div>
          )}
        </div>
        {type === "logo" && url && (
          <div className="group relative h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
            <img
              src={url}
              alt="Logo"
              className="h-full w-full object-contain"
            />
            <div
              className="absolute inset-0 flex cursor-pointer items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100"
              onClick={() => window.open(url, "_blank")}
            >
              <Globe className="h-3 w-3 text-white" />
            </div>
          </div>
        )}
        {type === "pdfs" && url && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0 border-slate-200 hover:bg-slate-50"
            onClick={() => window.open(url, "_blank")}
          >
            <FileText className="h-4 w-4 text-slate-500" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default FileUploadField;
